/**
 * The welcome tour (KPR-613, KPE-024): the first tour, for people new to keepr.
 * It walks the sidebar (the tab bar and the top bar on a phone) and ends at the
 * account menu, where it can be taken again. It is offered on Home and from the
 * account menu, never forced (`useWelcomeTour`, `contexts/WelcomeTourContext.js`).
 *
 * Every step is an `info` step that points at a real element of the app shell
 * (`src/Layout.js` carries the `data-tour` attributes), so the tour needs no
 * particular page: it runs the same from Home or from anywhere the account menu
 * is. The target-existence test renders the real shell against it
 * (`src/testUtils/tourTargets.js`, page `shell`).
 *
 * On a phone there is no sidebar: the same target names sit on the tab bar,
 * the top bar's bell and the Me tab. Discover and Pinned / Recent have no
 * place there (Discover is in the account menu; pins live on Home), so those
 * two steps carry a condition and are skipped at once, not after waiting for a
 * target. `compact` is handed in by `start()`.
 *
 * The Admin link is deliberately not a step: it is the one row only some
 * people have, and a tour must read the same for everyone.
 *
 * Copy follows docs/platform-standards.md section 11 (the words: item, card,
 * collection, sidebar, account menu, Search, inbox) and section 16 (a title is
 * a short sentence-case phrase, the text one or two short sentences, no
 * exclamation marks). Raise `version` when a step changes enough that people who
 * finished it should see it again; a person's record is kept per version.
 */
export const WELCOME_TOUR_ID = 'welcome';

const sidebarOnly = (context) => !(context && context.compact);

const welcomeTour = {
	id: WELCOME_TOUR_ID,
	version: 1,
	title: 'Welcome to keepr',
	audience: 'new',
	page: 'shell',
	steps: [
		{
			id: 'welcome',
			kind: 'info',
			title: 'Welcome to keepr',
			text: 'keepr keeps your items in collections. This takes about two minutes.',
		},
		{
			id: 'collections',
			target: 'nav-collections',
			kind: 'info',
			title: 'Collections',
			text: 'Your collections live here. Open one to see its items.',
		},
		{
			id: 'cards',
			target: 'nav-cards',
			kind: 'info',
			title: 'Cards',
			text: 'A card is the template for one kind of item. It lists the elements each item holds.',
		},
		{
			id: 'discover',
			target: 'nav-discover',
			kind: 'info',
			title: 'Discover',
			text: 'Ready-made collections and cards you can add to your own.',
			condition: sidebarOnly,
		},
		{
			id: 'search',
			target: 'search',
			kind: 'info',
			title: 'Search',
			text: 'Find any item, collection or action from anywhere. On a keyboard, press ⌘K or Ctrl+K.',
		},
		{
			id: 'pinned-recent',
			target: 'rail-lists',
			kind: 'info',
			title: 'Pinned and Recent',
			text: 'Pin what you use most and it waits here. Recent shows what you opened lately.',
			condition: sidebarOnly,
		},
		{
			id: 'notifications',
			target: 'notifications',
			kind: 'info',
			title: 'Notifications',
			text: 'Reminders and activity that need you arrive in your inbox.',
		},
		{
			id: 'account-menu',
			target: 'account-menu',
			kind: 'info',
			title: 'Your account menu',
			text: 'Take this tour again from here any time, and open the documentation.',
		},
	],
};

export default welcomeTour;
